import {
  BizNestClientError,
  EhttpController,
  FrontendLogger,
  IehttpControllerConfig
} from '@biz-nest/core-client';

/**
 * Main client class for handling commerce-related HTTP requests.
 * Provides a centralized way to interact with the commerce API endpoints.
 *
 * @example
 * ```typescript
 * import { CommerceClient } from '@biz-nest/commerce-client';
 *
 * // Initialize with axios config
 * const commerceClient = new CommerceClient({
 *   baseURL: 'https://api.example.com',
 *   timeout: 10000
 * });
 *
 * // Use the HTTP client
 * const http = CommerceClient.ehttp;
 * ```
 *
 * @class CommerceClient
 * @since 1.0.0
 */
export class CommerceClient {
  /** @internal */
  private static http: EhttpController;

  /**
   * Creates an instance of CommerceClient with comprehensive environment validation.
   *
   * @param {IehttpControllerConfig} config - Configuration object for the underlying HTTP client.
   * @throws {BizNestClientError} When the provided configuration is invalid or missing required properties.
   *
   * @example
   * ```typescript
   * const client = new CommerceClient({
   *   baseURL: 'https://api.example.com',
   *   headers: {
   *     'Authorization': 'Bearer token123'
   *   }
   * });
   * ```
   */
  constructor(config: IehttpControllerConfig) {
    if (!config || typeof config !== 'object') {
      throw new BizNestClientError('config must be a valid object');
    }

    FrontendLogger.debug('CommerceClient initialized');
    CommerceClient.http = new EhttpController(config);
  }

  /**
   * Static getter to access the HTTP controller instance.
   *
   * @returns {EhttpController} The HTTP controller instance.
   * @throws {BizNestClientError} When the HTTP client has not been initialized.
   *
   * @example
   * ```typescript
   * // Get the HTTP client
   * const http = CommerceClient.ehttp;
   *
   * // Make a GET request
   * const response = await http.get('/api/products');
   * ```
   *
   * @static
   * @readonly
   */
  static get ehttp(): EhttpController {
    if (!CommerceClient.http) {
      throw new BizNestClientError('HTTP client not initialized. Call new CommerceClient().');
    }

    return CommerceClient.http;
  }
}
